import { Component, Element, h, Host, Prop, State } from "@stencil/core";
import { debounce } from "../../utils/debounce";

@Component({
    tag: "ux-text-lines-limit",
    styleUrl: "text-lines-limit.scss",
    shadow: false
})
export class TextLinesLimit {
    private textElement: HTMLElement | null = null;
    private buttonBaseClassName = "ux-text-lines-limit__btn";
    private getIsReadMoreEnabled = () => typeof this.readMoreText === "string" && this.readMoreText.length > 0;

    @Element() element!: HTMLUxTextLinesLimitElement;

    /** Sets the default number of lines for all viewports */
    @Prop() readonly textLines: number | null = null;

    /** Sets the number of lines for small viewports */
    @Prop() readonly textLinesS: number = 0;

    /** Sets the number of lines for medium viewports */
    @Prop() readonly textLinesM: number = 0;

    /** Sets the number of lines for large viewports */
    @Prop() readonly textLinesL: number = 0;

    /** Sets the number of lines for xl viewports */
    @Prop() readonly textLinesXl: number = 0;

    /**
     * If this text is set, the component displays a button with the corresponding label.
     * When the user clicks on this button, the entire text is displayed and the button disappears.
     */
    @Prop() readonly readMoreText: string | undefined = undefined;

    /**
     * Custom class name assigned to the Read More button
     */
    @Prop() readonly readMoreClassName: string = "ux-text-link ux-text-link--green";

    /** @internal opened state */
    @State() opened: boolean;

    componentDidLoad() {
        this.update();
        const buttonElement = this.element.querySelector(`.${this.buttonBaseClassName}`);
        const buttonClassNames = [this.buttonBaseClassName, ...this.readMoreClassName.split(" ")];
        if (this.getIsReadMoreEnabled()) {
            if (!buttonElement) {
                this.textElement?.insertAdjacentHTML(
                    "beforeend",
                    `<button type="button" class="${buttonClassNames.join(" ")}">${this.readMoreText}</button>`
                );
            }
        } else if (buttonElement) {
            buttonElement.remove();
        }
    }

    connectedCallback() {
        this.setElements();

        if (this.getIsReadMoreEnabled()) {
            window.addEventListener("resize", this.updateDebounced);
        }
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.updateDebounced);
    }

    private setElements = () => {
        this.textElement = this.element.querySelector("*:first-child");
    };

    private getTextElementHeight = () => {
        const { textElement } = this;
        if (!textElement) {
            return 0;
        }
        const computedStyle = window.getComputedStyle(textElement);
        const clientHeight = textElement.clientHeight;
        return (
            clientHeight +
            parseFloat(computedStyle.getPropertyValue("margin-top")) +
            parseFloat(computedStyle.getPropertyValue("margin-bottom"))
        );
    };

    private updateExpansion = () => {
        this.element.style.removeProperty("--lines");
        /** increase (amount > 0) or decrease lines (amount < 0) */
        const adjustLinesStep = (amount: number) => {
            const lineCount = parseInt(window.getComputedStyle(this.element).getPropertyValue("--lines"), 10);
            this.element.style.setProperty("--lines", String(lineCount + amount));
        };
        // these values are only used to prevent infinite loops
        let counter = 0;
        const smallestLineHeight = 12;

        const expandIfPossible = () => {
            const availableHeight = this.element.clientHeight;
            if (availableHeight > this.getTextElementHeight()) {
                adjustLinesStep(1);
                if (availableHeight > this.getTextElementHeight()) {
                    if (counter++ > availableHeight / smallestLineHeight) return;
                    expandIfPossible();
                } else {
                    adjustLinesStep(-1);
                }
            }
        };
        expandIfPossible();
    };

    private updateCroppingFlag = () => {
        if (!this.textElement) {
            return;
        }
        const isCropped = this.textElement.scrollHeight > this.element.offsetHeight;
        if (isCropped) {
            this.element.removeAttribute("uncropped");
        } else {
            this.element.setAttribute("uncropped", "");
        }
    };

    private update = () => {
        this.updateExpansion();
        this.updateCroppingFlag();
    };

    private updateDebounced = debounce(this.update, 50);

    private handleClick = (e: Event) => {
        if (this.opened || !this.getIsReadMoreEnabled()) {
            return;
        }
        const button = (e.target as HTMLElement).closest(`.${this.buttonBaseClassName}`);
        if (button) {
            e.preventDefault();
            this.opened = true;
            this.element.setAttribute("open", "");
            window.removeEventListener("resize", this.updateDebounced);
        }
    };

    render() {
        const linesDefault = this.textLines || "unset";
        return (
            <Host
                onClick={this.getIsReadMoreEnabled() ? this.handleClick : null}
                open={this.opened || undefined}
                style={{
                    [`--lines-default`]: `${linesDefault}`,
                    [`--lines-s`]: `${this.textLinesS || linesDefault}`,
                    [`--lines-m`]: `${this.textLinesM || linesDefault}`,
                    [`--lines-l`]: `${this.textLinesL || linesDefault}`,
                    [`--lines-xl`]: `${this.textLinesXl || linesDefault}`
                }}
            >
                <slot />
            </Host>
        );
    }
}
